<script setup>
import { computed, inject } from 'vue';
import { columnLayout } from '../columnsLayout.js';
import { styleVars } from './shared.js';

// The template's own registry, provided by the page. Importing it
// from ./index.js would be a cycle (index.js imports this file).
const components = inject('tiqBlockComponents', null);
const editing = inject('tiqEditorCanvas', false);

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const layout = computed(() => columnLayout(props.config));
// Columns with no blocks in them are no block for a visitor; the editor
// keeps its "Empty column" labels (Mus, 2026-10-11).
const hasBlocks = computed(() => layout.value.columns.some((c) => c.blocks.length));
</script>

<template>
    <section v-if="hasBlocks || editing" :id="config.anchor_id || null" class="th-block th-sec th-cols" :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-cols__row"
                 :class="`th-cols__row--${layout.stackAt}`"
                 :style="{ '--th-cols-gap': layout.gapCss, alignItems: layout.align }">
                <div v-for="(col, i) in layout.columns" :key="i" class="th-cols__col" :style="{ flexGrow: col.grow }">
                    <template v-for="block in col.blocks" :key="block.id">
                        <div v-if="components && components[block.type]" class="th-cols__cell">
                            <component :is="components[block.type]"
                                       :config="block.config || {}"
                                       :accent="accent"
                                       :org="org"
                                       :site_data="site_data" />
                        </div>
                    </template>
                    <div v-if="!col.blocks.length && editing" class="th-cols__empty">Empty column</div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-cols__row { display: flex; flex-wrap: wrap; gap: var(--th-cols-gap); }
.th-cols__row--never { flex-wrap: nowrap; }
/* Each column grows by its share and never narrower than a phone
   column, so a three-up row folds to one on a phone on its own. */
.th-cols__col {
    flex: 1 1 0;
    min-width: min(100%, 220px);
    display: flex;
    flex-direction: column;
    gap: 24px;
    container-type: inline-size;
}
.th-cols__row--never .th-cols__col { min-width: 0; }
/* "Tablet and phone" folds a little earlier. */
.th-cols__row--tablet .th-cols__col { min-width: min(100%, 340px); }
.th-cols__cell { min-width: 0; }

/* A block inside a column is a card in a row, not a section of the
   page: it sheds the page gutter, the section padding and the wide
   headline, and keeps everything else it draws. */
.th-cols__cell :deep(.th-sec),
.th-cols__cell :deep(.th-band),
.th-cols__cell :deep(.th-sec--tight) { padding-top: 0; padding-bottom: 0; }
.th-cols__cell :deep(.th-wrap) { padding-left: 0; padding-right: 0; max-width: none; }
.th-cols__cell :deep(.th-head) { margin-bottom: 14px; }
.th-cols__cell :deep(h2) { font-size: 22px; line-height: 1.15; font-stretch: 106%; }
.th-cols__cell :deep(h1) { font-size: clamp(26px, 3vw, 34px); line-height: 1.02; }
/* A band with its own background (the newsletter's tint) becomes a
   card: the same colour, an inset, the site's corners. */
.th-cols__cell > :deep(section) { border-radius: var(--r); }
.th-cols__cell :deep(.th-wrap.th-band),
.th-cols__cell :deep(.th-wrap.th-band--short) { padding: 20px; }
/* Blocks that set a side-by-side gap for a full page keep a column's
   rhythm instead. */
.th-cols__cell :deep(.th-faq__inner),
.th-cols__cell :deep(.th-split) { gap: 14px; }
.th-cols__cell :deep(.th-eyebrow) { margin-bottom: 10px; }

.th-cols__empty {
    border: 1px dashed var(--th-field);
    border-radius: var(--r);
    padding: 24px;
    text-align: center;
    font: 400 12px/1 var(--th-mono);
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--th-faint);
}
</style>
